
//  ---------------------------------------------------------------------------

import binance from './binance.js';
import type { Endpoint, Dict, List } from './base/types.js';

//  ---------------------------------------------------------------------------

export default class binanceus extends binance {
    override describe (): any {
        return this.deepExtend (super.describe (), {
            'id': 'binanceus',
            'name': 'Binance US',
            'countries': [ 'US' ], // US
            'hostname': 'binance.us',
            'rateLimit': 50, // 1200 req per min
            'certified': false,
            'pro': true,
            'urls': {
                'logo': 'https://github.com/user-attachments/assets/a9667919-b632-4d52-a832-df89f8a35e8c',
                'api': {
                    'web': 'https://www.binance.us',
                    'public': 'https://api.binance.us/api/v3',
                    'private': 'https://api.binance.us/api/v3',
                    'sapi': 'https://api.binance.us/sapi/v1',
                    'sapiV2': 'https://api.binance.us/sapi/v2',
                    'sapiV3': 'https://api.binance.us/sapi/v3',
                },
                'www': 'https://www.binance.us',
                'referral': 'https://www.binance.us/?ref=35005074',
                'doc': 'https://github.com/binance-us/binance-official-api-docs',
                'fees': 'https://www.binance.us/en/fee/schedule',
            },
            'fees': {
                'trading': {
                    'tierBased': true,
                    'percentage': true,
                    'taker': this.parseNumber ('0.001'), // 0.1% trading fee, zero fees for all trading pairs before November 1.
                    'maker': this.parseNumber ('0.001'), // 0.1% trading fee, zero fees for all trading pairs before November 1.
                },
            },
            'options': {
                'fetchMarkets': {
                    'types': [ 'spot' ],
                },
                'defaultType': 'spot',
                'fetchMargins': false,
                'quoteOrderQty': false,
                'fetchCurrencies': false,
            },
            'has': {
                'CORS': undefined,
                'spot': true,
                'margin': false,
                'swap': false,
                'future': false,
                'option': false,
                'addMargin': false,
                'borrowCrossMargin': false,
                'borrowIsolatedMargin': false,
                'borrowMargin': false,
                'closeAllPositions': false,
                'closePosition': false,
                'createReduceOnlyOrder': false,
                'createStopLossOrder': false,
                'createTakeProfitOrder': false,
                'fetchAllGreeks': false,
                'fetchBorrowInterest': false,
                'fetchBorrowRate': false,
                'fetchBorrowRateHistories': false,
                'fetchBorrowRateHistory': false,
                'fetchBorrowRates': false,
                'fetchBorrowRatesPerSymbol': false,
                'fetchCrossBorrowRate': false,
                'fetchCrossBorrowRates': false,
                'fetchFundingHistory': false,
                'fetchFundingInterval': false,
                'fetchFundingIntervals': false,
                'fetchFundingRate': false,
                'fetchFundingRateHistory': false,
                'fetchFundingRates': false,
                'fetchGreeks': false,
                'fetchIndexOHLCV': false,
                'fetchIsolatedBorrowRate': false,
                'fetchIsolatedBorrowRates': false,
                'fetchIsolatedPositions': false,
                'fetchLeverage': false,
                'fetchLeverageTiers': false,
                'fetchLongShortRatio': false,
                'fetchLongShortRatioHistory': false,
                'fetchMarketLeverageTiers': false,
                'fetchMarkOHLCV': false,
                'fetchMarkPrice': false,
                'fetchMarkPrices': false,
                'fetchMySettlementHistory': false,
                'fetchOpenInterestHistory': false,
                'fetchOpenInterests': false,
                'fetchOption': false,
                'fetchOptionChain': false,
                'fetchPosition': false,
                'fetchPositionHistory': false,
                'fetchPositionMode': false,
                'fetchPositions': false,
                'fetchPositionsForSymbol': false,
                'fetchPositionsHistory': false,
                'fetchPositionsRisk': false,
                'fetchPremiumIndexOHLCV': false,
                'fetchSettlementHistory': false,
                'fetchUnderlyingAssets': false,
                'fetchVolatilityHistory': false,
                'reduceMargin': false,
                'repayCrossMargin': false,
                'repayIsolatedMargin': false,
                'setLeverage': false,
                'setMargin': false,
                'setMarginMode': false,
                'setPositionMode': false,
            },
            'api': {
                'public': {
                    'get': {
                        'ping': { 'cost': 1 } as Endpoint<Dict>,
                        'time': { 'cost': 1 } as Endpoint<Dict>,
                        'exchangeInfo': { 'cost': 10 } as Endpoint<Dict>,
                        'trades': { 'cost': 1 } as Endpoint<List>,
                        'historicalTrades': { 'cost': 5 } as Endpoint<List>,
                        'aggTrades': { 'cost': 1 } as Endpoint<List>,
                        'depth': { 'cost': 1, 'byLimit': [ [ 100, 1 ], [ 500, 5 ], [ 1000, 10 ], [ 5000, 50 ] ] } as Endpoint<Dict>,
                        'klines': { 'cost': 1 } as Endpoint<List>,
                        'ticker/price': { 'cost': 1, 'noSymbol': 2 } as Endpoint<Dict | List>,
                        'avgPrice': { 'cost': 1 } as Endpoint<Dict>,
                        'ticker/bookTicker': { 'cost': 1, 'noSymbol': 2 } as Endpoint<List>,
                        'ticker/24hr': { 'cost': 1, 'noSymbol': 40 } as Endpoint<Dict | List>,
                        'ticker': { 'cost': 2, 'noSymbol': 100 } as Endpoint<List>,
                    },
                },
                'private': {
                    'get': {
                        'account': { 'cost': 10 } as Endpoint<Dict>,
                        'rateLimit/order': { 'cost': 20 } as Endpoint<List>,
                        'order': { 'cost': 2 } as Endpoint<Dict>,
                        'openOrders': { 'cost': 3, 'noSymbol': 40 } as Endpoint<List>,
                        'myTrades': { 'cost': 10 } as Endpoint<List>,
                        'myPreventedMatches': { 'cost': 10 } as Endpoint<List>, // with ID it has weight 1, but we don't have that complex handling yet
                        'allOrders': { 'cost': 10 } as Endpoint<List>,
                        'orderList': { 'cost': 2 } as Endpoint<Dict>,
                        'allOrderList': { 'cost': 10 } as Endpoint<List>,
                        'openOrderList': { 'cost': 3 } as Endpoint<List>,
                    },
                    'post': {
                        'order': { 'cost': 1 } as Endpoint<Dict>,
                        'order/test': { 'cost': 1 } as Endpoint<Dict>,
                        'order/cancelReplace': { 'cost': 1 } as Endpoint<Dict>,
                        'order/oco': { 'cost': 1 } as Endpoint<Dict>,
                    },
                    'delete': {
                        'order': { 'cost': 1 } as Endpoint<Dict>,
                        'openOrders': { 'cost': 1 } as Endpoint<List>,
                        'orderList': { 'cost': 1 } as Endpoint<Dict>,
                    },
                },
                'sapi': {
                    'get': {
                        'system/status': { 'cost': 1 } as Endpoint<Dict>,
                        'asset/assetDistributionHistory': { 'cost': 1 } as Endpoint<Dict>,
                        'asset/query/trading-fee': { 'cost': 1 } as Endpoint<List>,
                        'asset/query/trading-volume': { 'cost': 1 } as Endpoint<Dict>,
                        'sub-account/spotSummary': { 'cost': 1 } as Endpoint<Dict>,
                        'sub-account/status': { 'cost': 1 } as Endpoint<List>,
                        'otc/coinPairs': { 'cost': 1 } as Endpoint<List>,
                        'otc/orders/{orderId}': { 'cost': 1 } as Endpoint<Dict>,
                        'otc/orders': { 'cost': 1 } as Endpoint<Dict>,
                        'ocbs/orders': { 'cost': 1 } as Endpoint<Dict>,
                        'capital/config/getall': { 'cost': 1 } as Endpoint<List>,
                        'capital/withdraw/history': { 'cost': 1 } as Endpoint<List>,
                        'fiatpayment/query/withdraw/history': { 'cost': 1 } as Endpoint<Dict>,
                        'capital/deposit/address': { 'cost': 1 } as Endpoint<Dict>,
                        'capital/deposit/hisrec': { 'cost': 1 } as Endpoint<List>,
                        'fiatpayment/query/deposit/history': { 'cost': 1 } as Endpoint<Dict>,
                        'capital/sub-account/deposit/address': { 'cost': 1 } as Endpoint<Dict>,
                        'capital/sub-account/deposit/history': { 'cost': 1 } as Endpoint<List>,
                        'asset/query/dust-logs': { 'cost': 1 } as Endpoint<Dict>,
                        'asset/query/dust-assets': { 'cost': 1 } as Endpoint<Dict>,
                        'marketing/referral/reward/history': { 'cost': 1 } as Endpoint<Dict>,
                        'staking/asset': { 'cost': 1 } as Endpoint<List>,
                        'staking/stakingBalance': { 'cost': 1 } as Endpoint<Dict>,
                        'staking/history': { 'cost': 1 } as Endpoint<List>,
                        'staking/stakingRewardsHistory': { 'cost': 1 } as Endpoint<Dict>,
                        'custodian/balance': { 'cost': 1 } as Endpoint<Dict>,
                        'custodian/supportedAssetList': { 'cost': 1 } as Endpoint<Dict>,
                        'custodian/walletTransferHistory': { 'cost': 1 } as Endpoint<Dict>,
                        'custodian/custodianTransferHistory': { 'cost': 1 } as Endpoint<Dict>,
                        'custodian/openOrders': { 'cost': 1 } as Endpoint<List>,
                        'custodian/order': { 'cost': 1 } as Endpoint<Dict>,
                        'custodian/orderHistory': { 'cost': 1 } as Endpoint<List>,
                        'custodian/tradeHistory': { 'cost': 1 } as Endpoint<List>,
                        'custodian/settlementSetting': { 'cost': 1 } as Endpoint<Dict>,
                        'custodian/settlementHistory': { 'cost': 1 } as Endpoint<Dict>,
                        'cl/transferHistory': { 'cost': 1 } as Endpoint<List>,
                        'apipartner/checkEligibility': { 'cost': 1 } as Endpoint<Dict>,
                        'apipartner/rebateHistory': { 'cost': 1 } as Endpoint<List>,
                    },
                    'post': {
                        'otc/quotes': { 'cost': 1 } as Endpoint<Dict>,
                        'otc/orders': { 'cost': 1 } as Endpoint<Dict>,
                        'fiatpayment/withdraw/apply': { 'cost': 1 } as Endpoint<Dict>,
                        'capital/withdraw/apply': { 'cost': 1 } as Endpoint<Dict>,
                        'asset/dust': { 'cost': 10 } as Endpoint<Dict>,
                        'staking/stake': { 'cost': 1 } as Endpoint<Dict>,
                        'staking/unstake': { 'cost': 1 } as Endpoint<Dict>,
                        'custodian/walletTransfer': { 'cost': 1 } as Endpoint<Dict>,
                        'custodian/custodianTransfer': { 'cost': 1 } as Endpoint<Dict>,
                        'custodian/undoTransfer': { 'cost': 1 } as Endpoint<Dict>,
                        'custodian/order': { 'cost': 1 } as Endpoint<Dict>,
                        'custodian/ocoOrder': { 'cost': 1 } as Endpoint<Dict>,
                        'cl/transfer': { 'cost': 1 } as Endpoint<Dict>,
                    },
                    'delete': {
                        'custodian/cancelOrder': { 'cost': 1 } as Endpoint<Dict>,
                        'custodian/cancelOrdersBySymbol': { 'cost': 1 } as Endpoint<List>,
                        'custodian/cancelOcoOrder': { 'cost': 1 } as Endpoint<Dict>,
                    },
                },
                'sapiV2': {
                    'get': {
                        'cl/account': { 'cost': 10 } as Endpoint<Dict>,
                        'cl/alertHistory': { 'cost': 1 } as Endpoint<List>,
                    },
                },
                'sapiV3': {
                    'get': {
                        'accountStatus': { 'cost': 1 } as Endpoint<Dict>,
                        'apiTradingStatus': { 'cost': 1 } as Endpoint<Dict>,
                        'sub-account/list': { 'cost': 1 } as Endpoint<Dict>,
                        'sub-account/transfer/history': { 'cost': 1 } as Endpoint<Dict>,
                        'sub-account/assets': { 'cost': 1 } as Endpoint<Dict>,
                    },
                    'post': {
                        'sub-account/transfer': { 'cost': 1 } as Endpoint<Dict>,
                    },
                },
            },
            'features': {
                'swap': {
                    'linear': undefined,
                    'inverse': undefined,
                },
                'future': {
                    'linear': undefined,
                    'inverse': undefined,
                },
            },
        });
    }
}

